import React, { useEffect, useMemo, useState } from "react";
import { listStudentMensurations, MENSURATION_LABELS } from "../../services/authService";

// Vue coach lecture seule des mensurations d'un élève.
// Isolée du MensurationsPanel du dashboard perso : ce dernier est calibré
// pour un fond sombre (chart en couleurs claires) ; ici on est sur fond clair
// et on n'a pas besoin de l'éditeur/upsert.
//
// Chart SVG inline (pas de dépendance), même logique que le panel perso
// mais avec palette adaptée au fond clair de CoachMode.

const isNum = (v) => v !== "" && v != null && !isNaN(parseFloat(v)) && parseFloat(v) > 0;

const formatDate = (ts) => {
	if (!ts) return "";
	const d = new Date(Number(ts));
	return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
};

function EvolutionChart({ points }) {
	if (!points || points.length < 2) {
		return (
			<div className="cs-mens-chart-empty">
				Pas assez de données pour tracer une courbe (au moins 2 mesures nécessaires).
			</div>
		);
	}

	const W = 640, H = 200, PAD_L = 44, PAD_R = 16, PAD_T = 18, PAD_B = 32;
	const values = points.map((p) => p.value);
	const timestamps = points.map((p) => p.ts);
	const minV = Math.min(...values);
	const maxV = Math.max(...values);
	const range = maxV - minV || 1;
	const tsMin = Math.min(...timestamps);
	const tsMax = Math.max(...timestamps);
	const tsRange = tsMax - tsMin || 1;

	const xFor = (ts) => PAD_L + ((ts - tsMin) / tsRange) * (W - PAD_L - PAD_R);
	const yFor = (v) => H - PAD_B - ((v - minV) / range) * (H - PAD_T - PAD_B);
	const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${xFor(p.ts)} ${yFor(p.value)}`).join(" ");

	// Formatte la valeur avec le nombre de décimales pertinent (0.1 précision
	// mais on cache le ".0" pour rester lisible).
	const fmt = (v) => Number.isInteger(v) ? String(v) : v.toFixed(1);

	// 4 ticks Y équirépartis dont min et max.
	const ticksY = [0, 0.33, 0.66, 1].map((t) => minV + range * t);
	// Labels X : premier et dernier point (assez d'info pour situer l'échelle).
	const firstDate = new Date(tsMin).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
	const lastDate = new Date(tsMax).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });

	return (
		<svg
			viewBox={`0 0 ${W} ${H}`}
			className="cs-mens-chart-svg"
			preserveAspectRatio="xMidYMid meet"
			style={{ width: "100%", height: "auto", maxHeight: 260 }}
		>
			{/* Grille horizontale discrète */}
			{ticksY.map((v, i) => {
				const y = yFor(v);
				return (
					<g key={i}>
						<line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="rgba(20,30,27,0.06)" strokeWidth="1" />
						<text x={PAD_L - 8} y={y + 3} fontSize="10" fill="var(--ink-faint)" textAnchor="end">
							{fmt(v)}
						</text>
					</g>
				);
			})}
			{/* Zone sous la courbe (fill léger pour donner du volume) */}
			<path
				d={`${path} L ${xFor(tsMax)} ${H - PAD_B} L ${xFor(tsMin)} ${H - PAD_B} Z`}
				fill="rgba(48,164,154,0.12)"
			/>
			{/* Courbe principale */}
			<path d={path} fill="none" stroke="#30a49a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
			{/* Points */}
			{points.map((p, i) => (
				<circle key={i} cx={xFor(p.ts)} cy={yFor(p.value)} r="3.5" fill="#30a49a" />
			))}
			{/* Labels X extrémités */}
			<text x={PAD_L} y={H - 10} fontSize="10" fill="var(--ink-faint)" textAnchor="start">{firstDate}</text>
			<text x={W - PAD_R} y={H - 10} fontSize="10" fill="var(--ink-faint)" textAnchor="end">{lastDate}</text>
		</svg>
	);
}

export default function CoachStudentMensurations({ student }) {
	const [mensurations, setMensurations] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	// Épaules par défaut (index 6) — indicateur haut du corps le plus suivi
	// en muscu, cohérent avec le default du panel perso.
	const [selectedMeasureIdx, setSelectedMeasureIdx] = useState(6);

	useEffect(() => {
		if (!student?.id) return;
		let cancelled = false;
		setLoading(true);
		setError("");
		listStudentMensurations(student.id).then((r) => {
			if (cancelled) return;
			if (r.success) setMensurations(r.mensurations);
			else setError(r.error || "Erreur");
			setLoading(false);
		});
		return () => { cancelled = true; };
	}, [student?.id]);

	const chartPoints = useMemo(() => {
		return mensurations
			.filter((m) => isNum(m.data?.[selectedMeasureIdx]))
			.map((m) => ({ ts: Number(m.date), value: parseFloat(m.data[selectedMeasureIdx]) }))
			.sort((a, b) => a.ts - b.ts);
	}, [mensurations, selectedMeasureIdx]);

	// Comparatif première vs dernière valeur de la mesure sélectionnée — utile
	// pour un coup d'œil rapide sans lire la courbe.
	const chartDelta = useMemo(() => {
		if (chartPoints.length < 2) return null;
		const first = chartPoints[0].value;
		const last = chartPoints[chartPoints.length - 1].value;
		const diff = last - first;
		return { first, last, diff, pct: (diff / first) * 100 };
	}, [chartPoints]);

	if (loading) {
		return <div className="cs-mens-empty">Chargement des mensurations…</div>;
	}
	if (error) {
		return <div className="mens-modal-error">{error}</div>;
	}
	if (mensurations.length === 0) {
		return (
			<div className="cs-mens-empty">
				<div className="cs-mens-empty-title">Aucune mensuration enregistrée</div>
				<div className="cs-mens-empty-hint">
					{student?.prenom || "L'élève"} n'a pas encore saisi de mensurations dans son app.
					C'est lui qui les renseigne depuis son écran Mensurations.
				</div>
			</div>
		);
	}

	return (
		<div className="cs-mens">
			<div className="cs-mens-header">
				<div>
					<h3 className="cs-mens-title">Mensurations</h3>
					<div className="cs-mens-sub">
						{mensurations.length} mesure{mensurations.length > 1 ? "s" : ""} enregistrée{mensurations.length > 1 ? "s" : ""}
						{mensurations[0]?.date && ` · dernière le ${formatDate(mensurations[0].date)}`}
					</div>
				</div>
				<span className="cs-mens-readonly-tag">Lecture seule</span>
			</div>

			<div className="cs-mens-section">
				<div className="cs-mens-section-head">
					<h4>Évolution</h4>
					<select
						value={selectedMeasureIdx}
						onChange={(e) => setSelectedMeasureIdx(parseInt(e.target.value, 10))}
						className="cs-mens-select"
					>
						{MENSURATION_LABELS.map((label, i) => (
							<option key={i} value={i}>{label}</option>
						))}
					</select>
				</div>
				{chartDelta && (
					<div className="cs-mens-delta">
						<span className="cs-mens-delta-val">
							{chartDelta.first.toFixed(1)} → <strong>{chartDelta.last.toFixed(1)} cm</strong>
						</span>
						<span className={`cs-mens-delta-chip ${chartDelta.diff > 0 ? "up" : chartDelta.diff < 0 ? "down" : "flat"}`}>
							{chartDelta.diff > 0 ? "+" : ""}{chartDelta.diff.toFixed(1)} cm
							{chartDelta.first > 0 && (
								<span className="cs-mens-delta-pct"> ({chartDelta.diff > 0 ? "+" : ""}{chartDelta.pct.toFixed(1)}%)</span>
							)}
						</span>
					</div>
				)}
				<EvolutionChart points={chartPoints} />
			</div>

			<div className="cs-mens-section">
				<h4>Historique</h4>
				<div className="cs-mens-timeline">
					{mensurations.map((m) => {
						const filled = (m.data || []).filter(isNum).length;
						return (
							<div key={m.id} className="cs-mens-row">
								<div className="cs-mens-row-head">
									<span className="cs-mens-row-date">{formatDate(m.date)}</span>
									<span className="cs-mens-row-count">{filled} mesure{filled > 1 ? "s" : ""}</span>
								</div>
								<div className="cs-mens-row-grid">
									{MENSURATION_LABELS.map((label, i) => {
										const v = m.data?.[i];
										if (!isNum(v)) return null;
										return (
											<div key={i} className="cs-mens-cell">
												<span className="cs-mens-cell-label">{label}</span>
												<span className="cs-mens-cell-value">{parseFloat(v).toFixed(1)}<em> cm</em></span>
											</div>
										);
									})}
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
